import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Paper, Pagination, Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Input, FormHelperText, Typography, Autocomplete, Select, MenuItem, RadioGroup, ToggleButton } from '@mui/material';
import { FormControlLabel, Radio, FormLabel } from '@mui/material'
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import "./App.css";
import { IconButton } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InputAdornment from '@mui/material/InputAdornment';
import SendIcon from '@mui/icons-material/Send';
import RequirementsList from './RequirementsList';
import * as XLSX from 'xlsx';
import image from "./assets/file_upload_icon.png"


const ChatBox = ({ fetchAPI, username, OpenHelpDrawer, handleNewChatButton, chatStarted, setChatStarted, messagePairs, setMessagePairs, activeChatID, loading, setLoading }) => {

    const [inputValue, setInputValue] = useState('');
    const [rule, setRule] = useState("incose")
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileExtension, setFileExtension] = useState('');
    const [switchEnabled, setSwitchEnabled] = useState(false);
    const fileInputRef = useRef(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [startDelimiter, setStartDelimiter] = useState('');
    const [endDelimiter, setEndDelimiter] = useState('');
    const [dialogLoader, setDialogLoader] = useState(false)
    const requirementsPerPage = 6;

    // New state for Excel file handling
    const [isExcelFile, setIsExcelFile] = useState(false);
    const [sheetNames, setSheetNames] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [columnNames, setColumnNames] = useState([]);
    const [selectedColumn, setSelectedColumn] = useState('');
    const [excelDialogStep, setExcelDialogStep] = useState(1); // 1 for sheet selection, 2 for column selection


    //  Handle Excel file processing
    useEffect(() => {
        if (selectedFile && isExcelFile) {
            setDialogLoader(true)
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheets = workbook.SheetNames;
                    setSheetNames(sheets);

                    // Reset selections when a new file is loaded
                    setSelectedSheet('');
                    setColumnNames([]);
                    setSelectedColumn('');
                    setExcelDialogStep(1);
                    setDialogLoader(false);

                } catch (error) {
                    console.error("Error reading Excel file:", error);
                    alert("Error reading Excel file. Please try another file.");
                    setDialogLoader(false);
                }
            };
            reader.readAsArrayBuffer(selectedFile);


        }
    }, [selectedFile, isExcelFile]);

    // load column names when sheet is selected
    useEffect(() => {
        if (selectedFile && isExcelFile && selectedSheet) {
            setDialogLoader(true)
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const worksheet = workbook.Sheets[selectedSheet];

                    // Convert to JSON to get column headers
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    if (jsonData.length > 0) {
                        // First row contains headers
                        const headers = jsonData[0];
                        setColumnNames(headers);
                    } else {
                        setColumnNames([]);
                    }
                    setDialogLoader(false)
                } catch (error) {
                    console.error("Error reading Excel sheet:", error);
                    setDialogLoader(false)
                }
            };

            reader.readAsArrayBuffer(selectedFile);
        }
    }, [selectedSheet, selectedFile, isExcelFile]);

    const generateQueryId = () => {
        // Format: q_YYYYMMDD_HHMMSS_XXX (XXX is milliseconds)
        const now = new Date();

        // Format date components with padding
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const millis = String(now.getMilliseconds()).padStart(3, '0');

        // Create timestamp in format that sorts lexicographically
        return `q_${year}${month}${day}_${hours}${minutes}${seconds}_${millis}`;
    };

    const process_requirement = async ({
        data_payload,
        onSuccess,
        onTimeout,
    }) => {
        const username = data_payload.username;
        const messageId = data_payload.query_id;
        const chat_id = data_payload.requestId
        try {
            const response = await fetchAPI.post("/process", data_payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
                }
            });


            if (response.data.statusCode === 200) {
                let pollCount = 0;
                const pollData = {
                    username,
                    checkFileStatus: true,
                    requestId: chat_id,
                    query_name: messageId
                };

                const pollForResult = async () => {
                    try {
                        pollCount++;
                        if (pollCount > 20) {
                            onTimeout?.();
                            return;
                        }

                        const result = await fetchAPI.post("/check_file_status", pollData, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
                            },
                        });

                        if (result.status === 200 && result.data.statusCode !== 500) {
                            const body = result.data.body;
                            onSuccess?.(body);
                        } else {
                            setTimeout(pollForResult, 3000);
                        }
                    } catch (err) {
                        console.error("Polling error:", err);
                        setTimeout(pollForResult, 3000);
                    }
                };

                pollForResult();
            }
        } catch (err) {
            console.error("Initial request error:", err);
            alert(err)
            setLoading(false);
        }
    };
    const uploadFile = async ({ data, onSuccess, onError }) => {
        try {
            const uploadResponse = await fetchAPI.post("/upload_file", data, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
                }
            })
            if (uploadResponse.status === 200) {
                console.log("File uploaded successfully");
                onSuccess?.(uploadResponse.data);
            }
            else {
                console.log("Issue in uploading file with status code", uploadResponse.status);
                onError(uploadResponse);
            }
        }
        catch (err) {
            console.error("Uploading file error:", err);
            if (err.response != undefined && err.response.status == 413) {
                onError(err.response.data)
            }
            else {
                alert(err)
            }
            setLoading(false);
        }
    }

    const callProcessEndpoint = async (data_payload, messageId, req_type, userMessage) => {

        await process_requirement({
            data_payload,
            onSuccess: (aiBody) => {
                setMessagePairs(prev => [
                    {
                        messageId,
                        req_type,
                        aiResponse: {
                            body: aiBody,
                            gotResponse: true,
                            question: userMessage
                        }
                    },
                    ...prev
                ]);
                setLoading(false);
            },
            onTimeout: () => {
                setMessagePairs(prev => [
                    {
                        messageId,
                        req_type,
                        aiResponse: {
                            body: "Given requirement is taking time to analyze, please split the requirement and retry",
                            gotResponse: false,
                        }
                    },
                    ...prev
                ]);
                setLoading(false);
            }
        });
    }

    const callUploadFileEndpoint = async (formData, messageId, req_type, chatId) => {
        await uploadFile({
            data: formData,
            onSuccess: async (response) => {
                // Get the execution ARN from the response
                const executionArn = response.executionArn;

                // Poll for completion using the execution ARN
                const pollData = {
                    username,
                    requestId: chatId,
                    executionArn: executionArn
                };

                let pollCount = 0;
                const pollForCompletion = async () => {
                    try {
                        pollCount++;
                        if (pollCount > 60) { // Allow more time for file processing
                            alert("File processing is taking longer than expected. Please try again with less number of requirements.");
                            setLoading(false);
                            return;
                        }

                        const result = await fetchAPI.post("/check_execution_status", pollData, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
                            },
                        });

                        if (result.status === 200 && result.data.status === "SUCCEEDED") {
                            // Processing complete - fetch the requirements list
                            try {
                                const requirementsResult = await fetchAPI.post("/get_file_requirements", {
                                    username,
                                    requestId: chatId,
                                    fileId: messageId
                                }, {
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
                                    },
                                });

                                if (requirementsResult.status === 200) {
                                    // Set the requirements list
                                    const requirements = requirementsResult.data.body || [];
                                    const aiResponse = {
                                        body: requirements,
                                        gotResponse: true
                                    }
                                    setMessagePairs(prev => [{
                                        messageId,
                                        req_type,
                                        aiResponse
                                    }, ...prev
                                    ])

                                } else {
                                    throw new Error("Failed to fetch requirements list");
                                }
                            } catch (error) {
                                console.error("Error fetching requirements:", error);
                                setMessagePairs(prev => [
                                    {
                                        messageId,
                                        req_type,
                                        aiResponse: {
                                            body: "File was processed but there was an error retrieving the requirements. Please try again.",
                                            gotResponse: false,
                                        }
                                    },
                                    ...prev
                                ]);
                            }
                            setLoading(false);
                        } else if (result.status === 200 && result.data.status === "FAILED") {
                            // Processing failed
                            setMessagePairs(prev => [
                                {
                                    messageId,
                                    req_type,
                                    aiResponse: {
                                        body: "There was an error processing your file. Please try again.",
                                        gotResponse: false,
                                    }
                                },
                                ...prev
                            ]);
                            setLoading(false);
                        } else {
                            // Still processing, continue polling
                            setTimeout(pollForCompletion, 5000);
                        }
                    } catch (err) {
                        console.error("Polling error:", err);
                        setTimeout(pollForCompletion, 5000);
                    }
                };

                // Start polling
                pollForCompletion();
            },
            onError: (error) => {
                console.log("Error in uploading file:", error);
                if (error.statusCode == 413) {
                    alert(error.message)
                }
                else {
                    alert("Error uploading file. Please try again.");
                }
                setLoading(false);
            }
        });
    };

    const handleSendMessage = async () => {

        if (!inputValue.trim()) return;
        console.log("message sent");

        const uniqueId = uuidv4();
        const userMessage = { text: inputValue, isUser: true };
        const messageId = generateQueryId();

        const messageText = inputValue;
        setInputValue('');

        let chatId = activeChatID;
        if (!chatId) {
            console.log("No active chat, creating one first");
            try {
                chatId = await handleNewChatButton();

                if (!chatId) {
                    console.log("Chat creation canceled");
                    return;
                }
            } catch (error) {
                console.error("Error creating new chat:", error);
                alert("Error creating new chat:", error)
                return;
            }
        }

        setChatStarted(true);
        setLoading(true);

        if (selectedFile) {

            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('username', username);
            formData.append('requestId', chatId);
            formData.append('file_id', messageId);
            formData.append('rule', rule)
            console.log("this is rule type", rule)

            if (isExcelFile) {
                formData.append('sheet_name', selectedSheet);
                formData.append('column_name', selectedColumn);
            } else {
                formData.append('start_delimiter', startDelimiter);
                if (endDelimiter) {
                    formData.append('end_delimiter', endDelimiter);
                }
            }
            console.log("FormData contents:");
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }
            callUploadFileEndpoint(formData, messageId, "multiple", chatId);
            // Reset form fields
            setStartDelimiter('');
            setEndDelimiter('');
            setSelectedSheet('');
            setSelectedColumn('');
            setSelectedFile(null);
            setIsExcelFile(false);
        }
        else {
            const data_payload = {
                username: username,
                requestedInput: messageText,
                requestId: chatId,
                query_id: messageId,
                rule: rule
            };

            callProcessEndpoint(data_payload, messageId, "single", messageText);
        }


    };
    // Modified to open dialog instead of directly selecting file
    const triggerFileInput = () => {
        setUploadDialogOpen(true);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Allowed extensions
            const max_limit = 25 * 1024 * 1024
            if (file.size > max_limit) {
                alert('Please select a file upto 20 MB size limit');
                event.target.value = null
            }
            else {
                const allowedExtensions = ['doc', 'docx', 'csv', 'xls', 'xlsx', 'txt'];

                // Extract extension from file name
                const fileExtension = file.name.split('.').pop().toLowerCase();

                if (allowedExtensions.includes(fileExtension)) {
                    setSelectedFile(file);
                    setFileExtension(fileExtension)
                    // Check if it's an Excel file
                    const isExcel = ['xls', 'xlsx'].includes(fileExtension);
                    setIsExcelFile(isExcel);
                    setInputValue(`File selected: ${file.name}`);
                } else {
                    alert('Please select a valid document or spreadsheet file (doc, docx, csv, xls, xlsx, txt)');
                }
            }

        }
    };
    // const triggerFileInput = () => {
    //     fileInputRef.current.click();
    // };
    const handleFileUnselect = () => {
        setSelectedFile(null);
        setInputValue('');
        setStartDelimiter('');
        setEndDelimiter('');
        setSelectedSheet('');
        setColumnNames([]);
        setSelectedColumn('');
        setIsExcelFile(false);
        setExcelDialogStep(1);
    };

    const handleUploadDialogClose = () => {
        setUploadDialogOpen(false);
        if (isExcelFile) {
            setSelectedSheet('');
            setColumnNames([]);
            setSelectedColumn('');
            setExcelDialogStep(1);
            setRule("incose");
        }
    };
    const handleSheetSelect = (event) => {
        setSelectedSheet(event.target.value);
        setExcelDialogStep(2);
    };

    const handleColumnSelect = (event) => {
        setSelectedColumn(event.target.value);
    };
    const handleUploadConfirm = () => {
        if (!selectedFile) {
            alert('Please select a file to upload');
            return;
        }

        if (isExcelFile) {
            if (!selectedSheet) {
                alert('Please select a sheet');
                return;
            }

            if (!selectedColumn) {
                alert('Please select a column');
                return;
            }

            setInputValue(`File: ${selectedFile.name} | Sheet: ${selectedSheet} | Column: ${selectedColumn}`);
        } else {
            if (!startDelimiter) {
                alert('Please specify a start delimiter');
                return;
            }

            setInputValue(`File selected: ${selectedFile.name}`);
        }

        setUploadDialogOpen(false);
        handleSendMessage();
    };

    const handleRadioChange = (radioVal) => {
        setRule(radioVal);
        setSwitchEnabled(radioVal == "incose" ? false : true)
    }
    const handleSwitchChange = (switchEnabled) => {
        setSwitchEnabled(switchEnabled);
        setRule(switchEnabled ? "ears" : "incose");
    }
    const CustomSwitch = ({ checked, onChange }) => {
        return (
            <Box
                onClick={() => onChange(!checked)}
                sx={{
                    position: 'relative',
                    width: '42px',
                    height: '26px',
                    backgroundColor: checked ? '#65C466' : '#E9E9EA',
                    borderRadius: '13px',
                    cursor: 'pointer',
                    transition: 'background-color 300ms ease',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '2px',
                        left: checked ? '18px' : '2px',
                        width: '22px',
                        height: '22px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'left 300ms ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                />
            </Box>

        );
    };


    return (
        <Box className="chat-container" style={{
            position: 'sticky',
            top: 0,
            width: OpenHelpDrawer ? '10%' : '100%',
            backgroundColor: "rgba(239, 243, 250, 0.87)",
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' // Hide overflow at container level
        }}>
            <Box className="chat-input-container" sx={{
                display: "flex",
                justifyContent: "center",
                width: "95%",
                padding: "10px",
                flexShrink: 0 // Prevent input from shrinking
            }}>
                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    accept=".doc,.docx,.csv,.xls,.xlsx,.txt"
                />
                <TextField
                    variant="outlined"
                    placeholder="Type your requirement..."
                    value={inputValue}
                    disabled={selectedFile ? true : false}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter") {
                            handleSendMessage();
                        }
                    }}
                    className="chat-input"
                    fullWidth
                    sx={{
                        backgroundColor: "white",
                        borderRadius: "24px", // Curved edges
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            paddingRight: "8px",
                        },
                    }}
                    InputProps={{

                        endAdornment: (
                            <InputAdornment position="end" >
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: '20px',
                                            color: '#152C73',
                                            fontWeight: 500,
                                            padding: '10px',
                                        }}
                                    >
                                        EARS
                                    </Typography>
                                    <CustomSwitch
                                        checked={switchEnabled}
                                        onChange={(newValue) => handleSwitchChange(newValue)}

                                    />
                                </Box>

                                {selectedFile && (

                                    <Chip
                                        label={selectedFile.name}
                                        onDelete={() => handleFileUnselect()}
                                        style={{
                                            backgroundColor: '#90d5ff',
                                            borderRadius: '20px',
                                            maxWidth: '300px',
                                            padding: '10px'
                                        }}
                                    />
                                )}

                                <IconButton onClick={triggerFileInput} sx={{ color: "#1976d2" }}>
                                    <AttachFileIcon sx={{ color: "#152C73" }} />
                                </IconButton>
                                <IconButton onClick={() => { handleSendMessage() }} sx={{ color: "#1976d2" }}>
                                    <SendIcon sx={{ color: "#152C73" }} />
                                </IconButton>

                            </InputAdornment>
                        ),
                    }}
                />

            </Box>
            {/* File Upload Dialog */}
            <Dialog open={uploadDialogOpen} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth >
                <DialogTitle style={{ alignSelf: 'center' }}>Upload Requirements File</DialogTitle>
                <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ border: "2px dashed", borderRadius: '5px', padding: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: '#eff3fade' }}>

                        <label htmlFor='file-upload' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                            <img src={image} style={{ width: "100px" }}></img>
                            <p style={{ color: '#3f51b5', marginTop: '3px' }}>Click to Upload/Attach file</p>
                        </label>
                        <p style={{ color: 'grey', marginTop: '0px' }}>(Allowed typed are xlsx , txt and docx)</p>

                        <FormControl fullWidth >
                            <input
                                id="file-upload"
                                type="file"
                                onChange={handleFileSelect}
                                accept=".doc,.docx,.xls,.xlsx,.txt"
                                hidden
                            />
                            {selectedFile && (
                                <FormHelperText>Selected: {selectedFile.name}</FormHelperText>
                            )}
                        </FormControl>

                    </Box>

                    {selectedFile && (
                        <>
                            {isExcelFile ? (
                                <>
                                    {/* Excel-specific UI */}
                                    <Typography variant="subtitle1" sx={{ paddingTop: '10px' }}>
                                        Excel File Options
                                    </Typography>

                                    {/* Step 1: Sheet Selection */}
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id="sheet-select-label">Select Sheet</InputLabel>
                                        <Select
                                            labelId="sheet-select-label"
                                            id="sheet-select"
                                            value={selectedSheet}
                                            onChange={handleSheetSelect}
                                            label="Select Sheet"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        '& .MuiMenuItem-root': {
                                                            display: 'block',   // force block layout
                                                            width: '100%',
                                                            padding: '5px'
                                                        }
                                                    }
                                                }
                                            }}

                                        >
                                            {sheetNames.map((sheet) => (
                                                <MenuItem key={sheet} value={sheet}>
                                                    {sheet}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            Select the sheet containing requirements
                                        </FormHelperText>
                                    </FormControl>

                                    {/* Step 2: Column Selection (only shown after sheet is selected) */}
                                    {excelDialogStep === 2 && (
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="column-select-label">Select Column</InputLabel>
                                            <Select
                                                labelId="column-select-label"
                                                id="column-select"
                                                value={selectedColumn}
                                                onChange={handleColumnSelect}
                                                label="Select Column"
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            '& .MuiMenuItem-root': {
                                                                display: 'block',   // force block layout
                                                                width: '100%',
                                                                padding: '5px'     // full width
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                {columnNames.map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                Select the column containing requirements
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Standard document delimiter UI */}
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel htmlFor="start-delimiter">Start Delimiter</InputLabel>
                                        <Input
                                            id="start-delimiter"
                                            value={startDelimiter}
                                            onChange={(e) => setStartDelimiter(e.target.value)}
                                            placeholder="e.g., [REQ]"
                                        />
                                        <FormHelperText>
                                            Text that marks the beginning of each requirement
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel htmlFor="end-delimiter">End Delimiter (Optional)</InputLabel>
                                        <Input
                                            id="end-delimiter"
                                            value={endDelimiter}
                                            onChange={(e) => setEndDelimiter(e.target.value)}
                                            placeholder="e.g., [/REQ]"
                                        />
                                        <FormHelperText>
                                            Text that marks the end of each requirement (if not specified, requirements end at the next start delimiter)
                                        </FormHelperText>
                                    </FormControl>
                                </>
                            )}
                            <FormControl margin='normal'>
                                <FormLabel id="demo-row-radio-buttons-group-label">The Response should follow</FormLabel>
                                <RadioGroup
                                    style={{ padding: '5px' }}
                                    row
                                    value={rule}
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    onChange={(e) => handleRadioChange(e.target.value)}
                                    name="row-radio-buttons-group">
                                    <FormControlLabel
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                paddingLeft: '8px'
                                            }
                                        }}
                                        value="incose"
                                        control={<Radio />}
                                        label="INCOSE" />
                                    <FormControlLabel
                                        value="ears"
                                        control={<Radio />}
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                paddingLeft: '8px'
                                            }
                                        }}
                                        label="EARS + INCOSE" />
                                </RadioGroup>
                            </FormControl>
                        </>

                    )

                    }

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUploadDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        disabled={
                            !selectedFile ||
                            (isExcelFile ? (!selectedSheet || !selectedColumn) : !startDelimiter)
                        }
                        onClick={handleUploadConfirm}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
                {dialogLoader && (
                    <div className="overlay">
                        <div className="loader"></div>
                    </div>
                )}
            </Dialog>
            {/* Messages container with single scrollbar */}
            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column', // Show newest messages at the bottom
                padding: '0 10px'
            }}>
                {/* Map through messages in reverse order */}
                {messagePairs.map((pair, index) => (
                    <Box key={pair.messageId} sx={{
                        marginBottom: '20px',
                        width: '95%',
                        maxWidth: '95%',
                        mx: 'auto',
                    }}>
                        {pair.req_type === "multiple" ? (
                            <Box id="file-requirements-section" sx={{ mt: 2, mb: 4, width: '100%', bgcolor: 'white' }}>
                                <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                                    <RequirementsList
                                        requirements={pair.aiResponse.body}
                                        requirementsPerPage={requirementsPerPage}
                                    />
                                </Paper>
                            </Box>
                        ) : (
                            <>
                                {/* For Single Requirement */}
                                {/* User Message */}
                                <Paper
                                    className="message"
                                    style={{
                                        backgroundColor: "#d3d2d2",
                                        alignSelf: "flex-end",
                                        padding: "6px",
                                        marginBottom: "10px",
                                        width: "100%",
                                        maxWidth: "100%",
                                        wordWrap: "break-word",
                                        display: "flex",
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: '#152c73!important',
                                            width: "3rem",
                                            height: '3rem',
                                            marginRight: '10px',
                                            marginTop: '5px',
                                            flexShrink: 0
                                        }}
                                    >
                                        U
                                    </Avatar>
                                    <Box
                                        display={'flex'}
                                        width={"calc(100% - 4rem)"}
                                        style={{
                                            color: "black",
                                            wordBreak: "break-word"
                                        }}
                                    >
                                        {pair.aiResponse && pair.aiResponse.question ? (
                                            <Box display={'flex'} width={"100%"} className="question-box">
                                                {pair.aiResponse.question}
                                            </Box>
                                        ) : (
                                            <p>Loading question...</p>
                                        )}
                                    </Box>
                                </Paper>

                                {/* AI Response */}
                                <Paper
                                    className="message"
                                    style={{
                                        backgroundColor: "#efefef",
                                        alignSelf: "flex-start",
                                        padding: "6px",
                                        width: "100%",
                                        maxWidth: "100%",
                                        wordWrap: "break-word",
                                        display: "flex",
                                        fontFamily: 'Roboto, sans-serif',
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: '#152c73!important',
                                            width: "3rem",
                                            height: '3rem',
                                            marginRight: '10px',
                                            marginTop: '5px',
                                            flexShrink: 0
                                        }}
                                    >
                                        AI
                                    </Avatar>

                                    {pair.aiResponse && pair.aiResponse.gotResponse ? (
                                        <Box sx={{
                                            width: "calc(100% - 4rem)",
                                            wordBreak: "break-word"
                                        }}>
                                            {/* <h4>Modified Requirement:</h4> */}
                                            {pair.aiResponse.body ? (
                                                <div style={{ width: "100%" }}>
                                                    <ReactMarkdown>{pair.aiResponse.body}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p>No Modified Requirement available</p>
                                            )}

                                            {/* {pair.aiResponse.body && pair.aiResponse.body.score_obj && (
                                                <>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                        <h4>Original Score:</h4>
                                                        <p style={{ alignSelf: 'center', marginLeft: '10px' }}>
                                                            {pair.aiResponse.body.score_obj.original_score}/100
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                        <h4>Revised Score:</h4>
                                                        <p style={{ alignSelf: 'center', marginLeft: '10px' }}>
                                                            {pair.aiResponse.body.score_obj.revised_score}/100
                                                        </p>
                                                    </div>
                                                    <h4>Reasoning:</h4>
                                                    <div style={{ width: "100%" }}>
                                                        <ReactMarkdown>{pair.aiResponse.body.score_obj.reasoning}</ReactMarkdown>
                                                    </div>
                                                </>
                                            )} */}
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            width: "calc(100% - 4rem)",
                                            wordBreak: "break-word"
                                        }}>
                                            {typeof pair.aiResponse.body === 'string' ? (
                                                <div style={{ width: "100%" }}>
                                                    <ReactMarkdown>{pair.aiResponse.body}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p>Processing your request...</p>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            </>
                        )}
                    </Box>
                ))}
            </Box>


            {
                loading && (
                    <div className="overlay">
                        <div className="loader"></div>
                    </div>
                )
            }
        </Box >
    );

}
export default ChatBox;